import React from 'react';
import { chunk } from 'lodash';
import { BackupDataCombined, CalculatedData, CalculatedDataResponse, dailyMonthlyData } from 'types';
import TooltipIcon from 'components/Tooltip/TooltipIcon';

const defaultCreditsResult = [0, 0, 0, 0, 0];
export const calculateYearlyDataGrow = (
    input: dailyMonthlyData[],
    key: 'target' | 'total',
    firstYearMonths: number,
): number[] => {
    let newInput: dailyMonthlyData[] = [];
    if (firstYearMonths < 12) {
        newInput = new Array(12 - firstYearMonths)
            .fill(0)
            .map(() => ({ creditsMonthly: 0, cumulatedCredits: 0, target: 0, total: 0 }));
    }
    newInput = [...newInput, ...input];
    return chunk(newInput, 12)
        .map((year) => year.map((values) => values[key]).reduce((acc, curr) => Number(acc) + Number(curr), 0))
        .map((output) => Math.round((output / 12) * 100) / 100);
};

export const calculateYearlyCredits = (input: dailyMonthlyData[], firstYearMonths: number): number[] => {
    let newInput: dailyMonthlyData[] = [];
    if (firstYearMonths < 12) {
        newInput = new Array(12 - firstYearMonths)
            .fill(0)
            .map(() => ({ creditsMonthly: 0, cumulatedCredits: 0, target: 0, total: 0 }));
    }
    newInput = [...newInput, ...input];

    return !input.length
        ? defaultCreditsResult
        : chunk(newInput, 12).map((year) =>
              year
                  .map((values) => values.creditsMonthly)
                  .reduce((acc, curr) => Math.round((acc + curr) * 100) / 100, 0),
          );
};

export const cumulateYearlyCredits = (
    hybridWorkloads: {
        creditsLtr: number[];
        creditsNoLtr: number[];
        dtc: number[];
    },
    cloudNativeWorkloads: {
        creditsLtr: number[];
        creditsNoLtr: number[];
        dtc: number[];
    },
    balanceCredits: number,
    creditsPercentage: any,
): { creditsTotal: { hwl: number[]; cwl: number[] }; yearWiseCreditBalance: { hwl: number[]; cwl: number[] } } => {
    let creditBalanceRemaining = balanceCredits;
    const yearWiseCreditBalance = { hwl: [], cwl: [] };
    const creditsTotal = { hwl: [], cwl: [] };

    const vCreditsLtrHwl = hybridWorkloads.creditsLtr.map(
        (credits, index) => Math.round((credits + hybridWorkloads.creditsNoLtr[index]) * 100) / 100,
    );
    const vCreditsLtrCwl = cloudNativeWorkloads.creditsLtr.map(
        (credits, index) => Math.round((credits + cloudNativeWorkloads.creditsNoLtr[index]) * 100) / 100,
    );

    vCreditsLtrHwl.forEach((credits, index) => {
        let creditBalanceForCurrentYearCwl;
        let creditBalanceForCurrentYearHwl;

        // Calculate the usable credit balance for both using Average Source+Changes percentage
        const usableCreditBalanceHwl = creditBalanceRemaining * (creditsPercentage[index]?.hwl || 0);
        const usableCreditBalanceCwl = creditBalanceRemaining * (creditsPercentage[index]?.cwl || 0);

        // Calculate total credits for both Hybrid and Cloud Native Workloads
        const totalCreditsHwl = credits + hybridWorkloads.dtc[index];
        const totalCreditsCwl = vCreditsLtrCwl[index] + cloudNativeWorkloads.dtc[index];

        let additionalCreditsRemainingInHWL = 0;
        let additionalCreditsRemainingInCWL = 0;

        // This is to handle the case where the total credits are less than the usable credit balance
        // So we can utilise the remaining credits for other workload
        // For e.g if totalCreditsHwl is 100 and usableCreditBalanceHwl is 150 and totalCreditsCwl is 290 and usableCreditBalanceCwl is 200
        // Then additionalCreditsRemainingInHWL will be 50 and total usableCreditBalanceCwl will be 250
        // hence total credits for cwl after deduction will be 290 - 250 = 40
        // and total credits for hwl after deduction will be 100 - 100 = 0
        // This way we can maximise the credits split for both workloads
        if (totalCreditsHwl < usableCreditBalanceHwl) {
            additionalCreditsRemainingInHWL = usableCreditBalanceHwl - totalCreditsHwl;
        }
        if (totalCreditsCwl < usableCreditBalanceCwl) {
            additionalCreditsRemainingInCWL = usableCreditBalanceCwl - totalCreditsCwl;
        }

        // calculate credits after deducting the usable credit balance and additional credits from other workload (for HWL it is CWL and vice versa)
        let deductedTotalCreditsHwl = totalCreditsHwl - usableCreditBalanceHwl - additionalCreditsRemainingInCWL;
        let deductedTotalCreditsCwl = totalCreditsCwl - usableCreditBalanceCwl - additionalCreditsRemainingInHWL;

        // Calculation for Hybrid Workloads
        if (deductedTotalCreditsHwl < 0) {
            // For Current year, credit balance will be credits + hybridWorkloads.dtc[index] as deductedTotalCreditsHwl < 0 after deducting credit balance
            creditBalanceForCurrentYearHwl = credits + hybridWorkloads.dtc[index];
        } else {
            // For Current year, credit balance will be usableCreditBalanceHwl + additionalCreditsRemainingInCWL as deductedTotalCreditsHwl >= 0 after deducting credit balance
            creditBalanceForCurrentYearHwl = usableCreditBalanceHwl + additionalCreditsRemainingInCWL;
        }

        // Calculation for Cloud Native Workloads
        if (deductedTotalCreditsCwl < 0) {
            // For Current year, credit balance will be vCreditsLtrCwl[index] + cloudNativeWorkloads.dtc[index] as deductedTotalCreditsCwl < 0 after deducting credit balance
            creditBalanceForCurrentYearCwl = vCreditsLtrCwl[index] + cloudNativeWorkloads.dtc[index];
        } else {
            // For Current year, credit balance will be usableCreditBalanceCwl + additionalCreditsRemainingInHWL as deductedTotalCreditsCwl >= 0 after deducting credit balance
            creditBalanceForCurrentYearCwl = usableCreditBalanceCwl + additionalCreditsRemainingInHWL;
        }

        // If deducted total credits are less than 0, set it to 0
        if (deductedTotalCreditsHwl < 0) {
            deductedTotalCreditsHwl = 0;
        }
        if (deductedTotalCreditsCwl < 0) {
            deductedTotalCreditsCwl = 0;
        }

        // Set credit balance to 0 as balance can carry forward only 1 year
        creditBalanceRemaining = 0;

        yearWiseCreditBalance.hwl.push(Math.round(creditBalanceForCurrentYearHwl * 100) / 100);
        yearWiseCreditBalance.cwl.push(Math.round(creditBalanceForCurrentYearCwl * 100) / 100);

        creditsTotal.hwl.push(Math.round(deductedTotalCreditsHwl * 100) / 100);
        creditsTotal.cwl.push(Math.round(deductedTotalCreditsCwl * 100) / 100);
    });
    return { creditsTotal, yearWiseCreditBalance };
};

const calculateResults = (results: CalculatedData, firstYearMonths: number, customerType: string) => {
    const [creditsLtr, creditsNoLtr, dtc] = [
        calculateYearlyCredits(results.ltr.monthlyData, firstYearMonths),
        calculateYearlyCredits(results.noLtr.monthlyData, firstYearMonths),
        calculateYearlyCredits(results.dtc.monthlyData, firstYearMonths),
    ];

    const creditsTotalYearly = creditsNoLtr.map(
        (noLtr, index) => Math.round((noLtr + creditsLtr[index] + dtc[index]) * 100) / 100,
    );

    // NOTE: creditsTotalCumulative will be calculated from final creditsTotal in calculateAllResults
    const creditsTotalCumulative = [0, 0, 0, 0, 0]; // Placeholder, will be overridden

    return {
        sourceChanges: calculateYearlyDataGrow(results.allSources.monthlyData, 'total', firstYearMonths),
        targetData: calculateYearlyDataGrow(results.allSources.monthlyData, 'target', firstYearMonths),
        creditsNoLtr,
        creditsLtr,
        dtc,
        creditsTotalYearly,
        creditsTotalCumulative,
    };
};

export const calculateAllResults = (
    { results }: CalculatedDataResponse,
    balanceCredits,
    customerType,
    firstYearMonths,
): BackupDataCombined => {
    const hybridWorkloads = calculateResults(results.hybridWorkloads, firstYearMonths, customerType);
    const cloudNativeWorkloads = calculateResults(results.cloudNativeWorkloads, firstYearMonths, customerType);

    const totalSourceChanges = hybridWorkloads.sourceChanges.map(
        (item, index) => item + cloudNativeWorkloads.sourceChanges[index],
    );

    // Calculate the percentage share of Source+Changes for both Hybrid and Cloud Native Workloads
    const creditsPercentage = totalSourceChanges.map((item, index) => ({
        hwl: hybridWorkloads.sourceChanges[index] / item,
        cwl: cloudNativeWorkloads.sourceChanges[index] / item,
    }));

    const { creditsTotal, yearWiseCreditBalance } = cumulateYearlyCredits(
        hybridWorkloads,
        cloudNativeWorkloads,
        balanceCredits,
        creditsPercentage,
    );

    // Create cumulative arrays for each workload type
    const hwlCumulative = creditsTotal.hwl.reduce((acc, hwlCredits, index) => {
        if (index === 0) {
            acc.push(Number(hwlCredits.toFixed(2)));
        } else {
            acc.push(Number((hwlCredits + acc[index - 1]).toFixed(2)));
        }
        return acc;
    }, [] as number[]);

    const cwlCumulative = creditsTotal.cwl.reduce((acc, cwlCredits, index) => {
        if (index === 0) {
            acc.push(Number(cwlCredits.toFixed(2)));
        } else {
            acc.push(Number((cwlCredits + acc[index - 1]).toFixed(2)));
        }
        return acc;
    }, [] as number[]);

    return {
        hybridWorkloads: {
            ...hybridWorkloads,
            creditsTotal: creditsTotal.hwl,
            creditsTotalCumulative: hwlCumulative,
            ...(customerType === 'Existing'
                ? {
                      creditsBalance: yearWiseCreditBalance.hwl,
                  }
                : { creditsBalance: [0, 0, 0, 0, 0] }),
        },
        cloudNativeWorkloads: {
            ...cloudNativeWorkloads,
            creditsTotal: creditsTotal.cwl,
            creditsTotalCumulative: cwlCumulative,
            ...(customerType === 'Existing'
                ? {
                      creditsBalance: yearWiseCreditBalance.cwl,
                  }
                : { creditsBalance: [0, 0, 0, 0, 0] }),
        },
    };
};

export const calculateBackupAcv = (discount: number, pricing: number, creditsTotal: number): number => {
    const discountDecimal = discount / 100;
    return Math.round(pricing * (1 - discountDecimal) * creditsTotal * 100) / 100 || 0;
};
export const calculateEffectivePrice = (totalCost: number, target: number): number => {
    if (target === 0) {
        return 0;
    }
    return Math.round((totalCost / target / 12) * 100) / 100;
};

export const createTableDataObj = (
    title: string,
    data: { hybridWorkloads: number[]; cloudNativeWorkloads: number[] },
): any => {
    let obj = {};
    const isCreditBalance = ['Rollover Credits to carry forward', 'Credits Deficit'].includes(title);
    const maxLength = Math.max(data.hybridWorkloads.length, data.cloudNativeWorkloads.length);
    for (let i = 0; i < maxLength; i += 1) {
        if (isCreditBalance) {
            const sum = Number(data.hybridWorkloads[i] + data.cloudNativeWorkloads[i]).toFixed(2);
            obj = {
                ...obj,
                [`${i}-HWL`]: data.hybridWorkloads[i] || '--',
                [`${i}-CWL`]: data.cloudNativeWorkloads[i] || '--',
                [`${i}-Total`]: sum === '0.00' ? '--' : sum,
                title,
            };
        } else {
            obj = {
                ...obj,
                [`${i}-HWL`]: data.hybridWorkloads[i] || 0,
                [`${i}-CWL`]: data.cloudNativeWorkloads[i] || 0,
                [`${i}-Total`]: Number((data.cloudNativeWorkloads[i] || 0) + (data.hybridWorkloads[i] || 0)).toFixed(2),
                title,
            };
        }
    }
    return obj;
};
export const calculatePriceByCloud = (planPrice: number, cloud: string, govCloudAddon: number): number =>
    cloud === 'govCloud' ? planPrice + govCloudAddon : planPrice;

export const createDiscountsTableDataObj = (
    data: { title: string; transform: (input, target) => number }[],
    { cloudNativeWorkloads, hybridWorkloads }: any,
    discounts: number[],
    planPrice: any,
): {
    title: string;
}[] =>
    data.map((obj) => {
        let tableObj = { title: obj.title };

        discounts.forEach((_, index) => {
            const hwl = obj.transform(
                calculateBackupAcv(
                    discounts[index],
                    planPrice.hybridWorkloads,
                    hybridWorkloads.creditsTotal[index] || 0,
                ),
                hybridWorkloads.targetData[index] || 0,
            );
            const cwl = obj.transform(
                calculateBackupAcv(
                    discounts[index],
                    planPrice.cloudNativeWorkloads,
                    cloudNativeWorkloads.creditsTotal[index] || 0,
                ),
                cloudNativeWorkloads.targetData[index] || 0,
            );

            tableObj = {
                ...tableObj,
                [`${index}-HWL`]: `$${hwl}`,
                [`${index}-CWL`]: `$${cwl}`,
                [`${index}-Total`]: `$${(hwl + cwl).toFixed(2)}`,
            };
        });
        return tableObj;
    });

export const createColumns = (
    title: string,
    iterator: Array<string | number>,
    unit = '',
    cells = [],
    onEditClick = null,
): Array<{ Header: React.ReactNode; accessor: string; subHeader?: any; Cell?: (data: any) => any }> => {
    const columns = [
        {
            Header: onEditClick ? (
                <>
                    {title}
                    <button type="button" className="discounts-edit qa-btn-discounts-edit" onClick={onEditClick}>
                        Edit
                    </button>
                </>
            ) : (
                title
            ),
            accessor: 'title',
            Cell: (data) => {
                const {
                    cell: { value },
                    row: { index },
                } = data;
                const tooltipData = cells[index];
                if (tooltipData?.tooltip)
                    return (
                        <span className="cell-with-tooltip">
                            {value}
                            <TooltipIcon
                                title={tooltipData.title}
                                placement={tooltipData.placement}
                                arrow={tooltipData.arrow}
                            />
                        </span>
                    );
                return value;
            },
        },
    ];
    // onEditClick is used to edit the discounts and thus can be used as boolean flag for discounts row
    if (onEditClick) {
        const restColumns = iterator?.reduce(
            (acc, crr, index) => [
                ...acc,
                {
                    Header: '--',
                    accessor: `${index}-CWL`,
                },
                {
                    Header: '--',
                    accessor: `${index}-HWL`,
                },
                {
                    Header: `${crr}${unit}`,
                    accessor: `${index}-Total`,
                },
            ],
            [],
        );
        return [...columns, ...restColumns];
    }

    const restColumns = iterator.map((iteration, index) => ({
        Header: `${iteration}${unit}`,
        accessor: `${index}`,
        columns: [
            {
                Header: `CWL`,
                accessor: `${index}-CWL`,
            },
            {
                Header: `HWL`,
                accessor: `${index}-HWL`,
            },
            {
                Header: `Total`,
                accessor: `${index}-Total`,
            },
        ],
    }));
    return [...columns, ...restColumns];
};

export const mergeDailyOrMonthlyData = (hybridWorkloadsData: any[], cloudNativeWorkloadsData: any[]) => {
    if (!hybridWorkloadsData.length) return cloudNativeWorkloadsData;
    if (!cloudNativeWorkloadsData.length) return hybridWorkloadsData;

    const maxLength = Math.max(hybridWorkloadsData.length, cloudNativeWorkloadsData.length);
    const mergedData = [];
    for (let i = 0; i < maxLength; i += 1) {
        const item1 = hybridWorkloadsData[i] || {};
        const item2 = cloudNativeWorkloadsData[i] || {};
        mergedData.push({
            totalDaily: Number((item1.totalDaily || 0) + (item2.totalDaily || 0)).toFixed(2),
            targetTbDaily: Number((item1.targetTbDaily || 0) + (item2.targetTbDaily || 0)).toFixed(2),
            total: Number((item1.total || 0) + (item2.total || 0)).toFixed(2),
            target: Number((item1.target || 0) + (item2.target || 0)).toFixed(2),
            creditsMonthly: Number((item1.creditsMonthly || 0) + (item2.creditsMonthly || 0)).toFixed(2),
            cumulatedCredits: Number((item1.cumulatedCredits || 0) + (item2.cumulatedCredits || 0)).toFixed(2),
        });
    }
    return mergedData;
};
